/**
 * Takeoff API Service - RTK Query
 * BuilderBel Construction Project Management System
 *
 * Pre-Construction module API endpoints for takeoffs, measurements, and assemblies.
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  Takeoff,
  TakeoffMeasurement,
  TakeoffAssembly,
  TakeoffSummary,
  TakeoffListResponse,
  MeasurementListResponse,
  AssemblyListResponse,
  TakeoffFilterParams,
  MeasurementFilterParams,
  AssemblyFilterParams,
  CreateTakeoffRequest,
  UpdateTakeoffRequest,
  CalibrateScaleRequest,
  CreateMeasurementRequest,
  UpdateMeasurementRequest,
  BulkCreateMeasurementsRequest,
  BulkUpdateCostCodesRequest,
  CreateAssemblyRequest,
  UpdateAssemblyRequest,
  ApplyAssemblyRequest,
  ExportToEstimateRequest,
} from '../types/takeoff';

export const takeoffApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ========================================================================
    // TAKEOFF CRUD
    // ========================================================================

    getTakeoffs: builder.query<
      ApiResponse<TakeoffListResponse>,
      { projectId: string; filters?: TakeoffFilterParams }
    >({
      query: ({ projectId, filters }) => ({
        url: `/projects/${projectId}/takeoffs`,
        params: filters,
      }),
      providesTags: (result, _error, { projectId }) =>
        result?.data?.takeoffs
          ? [
              ...result.data.takeoffs.map(({ _id }) => ({
                type: 'Takeoff' as const,
                id: _id,
              })),
              { type: 'Takeoff', id: `LIST_${projectId}` },
            ]
          : [{ type: 'Takeoff', id: `LIST_${projectId}` }],
    }),

    getTakeoff: builder.query<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) =>
        `/projects/${projectId}/takeoffs/${takeoffId}`,
      providesTags: (_result, _error, { takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
      ],
    }),

    createTakeoff: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; data: CreateTakeoffRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/takeoffs`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Takeoff', id: `LIST_${projectId}` },
      ],
    }),

    updateTakeoff: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string; data: UpdateTakeoffRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    deleteTakeoff: builder.mutation<
      ApiResponse<void>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================================================
    // SCALE CALIBRATION
    // ========================================================================

    calibrateScale: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string; data: CalibrateScaleRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/calibrate`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    // ========================================================================
    // WORKFLOW
    // ========================================================================

    completeTakeoff: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
      ],
    }),

    submitTakeoffForReview: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/submit-for-review`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
      ],
    }),

    approveTakeoff: builder.mutation<
      ApiResponse<Takeoff>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================================================
    // SUMMARY / REPORTS
    // ========================================================================

    getTakeoffSummary: builder.query<
      ApiResponse<TakeoffSummary>,
      { projectId: string; takeoffId: string }
    >({
      query: ({ projectId, takeoffId }) =>
        `/projects/${projectId}/takeoffs/${takeoffId}/summary`,
      providesTags: (_result, _error, { takeoffId }) => [
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    // ========================================================================
    // MEASUREMENTS
    // ========================================================================

    getMeasurements: builder.query<
      ApiResponse<MeasurementListResponse>,
      { projectId: string; takeoffId: string; filters?: MeasurementFilterParams }
    >({
      query: ({ projectId, takeoffId, filters }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements`,
        params: filters,
      }),
      providesTags: (result, _error, { takeoffId }) =>
        result?.data?.measurements
          ? [
              ...result.data.measurements.map(({ _id }) => ({
                type: 'TakeoffMeasurement' as const,
                id: _id,
              })),
              { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
            ]
          : [{ type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` }],
    }),

    getMeasurement: builder.query<
      ApiResponse<TakeoffMeasurement>,
      { projectId: string; takeoffId: string; measurementId: string }
    >({
      query: ({ projectId, takeoffId, measurementId }) =>
        `/projects/${projectId}/takeoffs/${takeoffId}/measurements/${measurementId}`,
      providesTags: (_result, _error, { measurementId }) => [
        { type: 'TakeoffMeasurement', id: measurementId },
      ],
    }),

    createMeasurement: builder.mutation<
      ApiResponse<TakeoffMeasurement>,
      { projectId: string; takeoffId: string; data: CreateMeasurementRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    bulkCreateMeasurements: builder.mutation<
      ApiResponse<{ created: number; measurements: TakeoffMeasurement[] }>,
      { projectId: string; takeoffId: string; data: BulkCreateMeasurementsRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements/bulk`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    updateMeasurement: builder.mutation<
      ApiResponse<TakeoffMeasurement>,
      {
        projectId: string;
        takeoffId: string;
        measurementId: string;
        data: UpdateMeasurementRequest;
      }
    >({
      query: ({ projectId, takeoffId, measurementId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements/${measurementId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId, measurementId }) => [
        { type: 'TakeoffMeasurement', id: measurementId },
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    deleteMeasurement: builder.mutation<
      ApiResponse<void>,
      { projectId: string; takeoffId: string; measurementId: string }
    >({
      query: ({ projectId, takeoffId, measurementId }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements/${measurementId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId, measurementId }) => [
        { type: 'TakeoffMeasurement', id: measurementId },
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    bulkUpdateCostCodes: builder.mutation<
      ApiResponse<{ modifiedCount: number }>,
      { projectId: string; takeoffId: string; data: BulkUpdateCostCodesRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/measurements/bulk-update-cost-codes`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { takeoffId }) => [
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    // ========================================================================
    // ASSEMBLIES
    // ========================================================================

    getAssemblies: builder.query<
      ApiResponse<AssemblyListResponse>,
      { projectId: string; filters?: AssemblyFilterParams }
    >({
      query: ({ projectId, filters }) => ({
        url: `/projects/${projectId}/takeoffs/assemblies`,
        params: filters,
      }),
      providesTags: (result, _error, { projectId }) =>
        result?.data?.assemblies
          ? [
              ...result.data.assemblies.map(({ _id }) => ({
                type: 'TakeoffAssembly' as const,
                id: _id,
              })),
              { type: 'TakeoffAssembly', id: `LIST_${projectId}` },
            ]
          : [{ type: 'TakeoffAssembly', id: `LIST_${projectId}` }],
    }),

    getAssembly: builder.query<
      ApiResponse<TakeoffAssembly>,
      { projectId: string; assemblyId: string }
    >({
      query: ({ projectId, assemblyId }) =>
        `/projects/${projectId}/takeoffs/assemblies/${assemblyId}`,
      providesTags: (_result, _error, { assemblyId }) => [
        { type: 'TakeoffAssembly', id: assemblyId },
      ],
    }),

    createAssembly: builder.mutation<
      ApiResponse<TakeoffAssembly>,
      { projectId: string; data: CreateAssemblyRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/takeoffs/assemblies`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'TakeoffAssembly', id: `LIST_${projectId}` },
      ],
    }),

    updateAssembly: builder.mutation<
      ApiResponse<TakeoffAssembly>,
      { projectId: string; assemblyId: string; data: UpdateAssemblyRequest }
    >({
      query: ({ projectId, assemblyId, data }) => ({
        url: `/projects/${projectId}/takeoffs/assemblies/${assemblyId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, assemblyId }) => [
        { type: 'TakeoffAssembly', id: assemblyId },
        { type: 'TakeoffAssembly', id: `LIST_${projectId}` },
      ],
    }),

    deleteAssembly: builder.mutation<
      ApiResponse<void>,
      { projectId: string; assemblyId: string }
    >({
      query: ({ projectId, assemblyId }) => ({
        url: `/projects/${projectId}/takeoffs/assemblies/${assemblyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId, assemblyId }) => [
        { type: 'TakeoffAssembly', id: assemblyId },
        { type: 'TakeoffAssembly', id: `LIST_${projectId}` },
      ],
    }),

    // ========================================================================
    // ASSEMBLY APPLICATION
    // ========================================================================

    applyAssembly: builder.mutation<
      ApiResponse<{ measurements: TakeoffMeasurement[]; assemblyInstanceId: string }>,
      { projectId: string; takeoffId: string; data: ApplyAssemblyRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/apply-assembly`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'TakeoffMeasurement', id: `LIST_${takeoffId}` },
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        { type: 'TakeoffSummary', id: takeoffId },
      ],
    }),

    // ========================================================================
    // EXPORT
    // ========================================================================

    exportToEstimate: builder.mutation<
      ApiResponse<{ estimateId: string }>,
      { projectId: string; takeoffId: string; data?: ExportToEstimateRequest }
    >({
      query: ({ projectId, takeoffId, data }) => ({
        url: `/projects/${projectId}/takeoffs/${takeoffId}/export-to-estimate`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { projectId, takeoffId }) => [
        { type: 'Takeoff', id: takeoffId },
        { type: 'Takeoff', id: `LIST_${projectId}` },
        // Cross-module: invalidate estimate list since export creates an estimate
        { type: 'Estimate', id: `LIST_${projectId}` },
      ],
    }),
  }),
});

/**
 * Export hooks for use in components
 */
export const {
  // Takeoff CRUD
  useGetTakeoffsQuery,
  useLazyGetTakeoffsQuery,
  useGetTakeoffQuery,
  useLazyGetTakeoffQuery,
  useCreateTakeoffMutation,
  useUpdateTakeoffMutation,
  useDeleteTakeoffMutation,

  // Scale
  useCalibrateScaleMutation,

  // Workflow
  useCompleteTakeoffMutation,
  useSubmitTakeoffForReviewMutation,
  useApproveTakeoffMutation,

  // Summary
  useGetTakeoffSummaryQuery,
  useLazyGetTakeoffSummaryQuery,

  // Measurements
  useGetMeasurementsQuery,
  useLazyGetMeasurementsQuery,
  useGetMeasurementQuery,
  useCreateMeasurementMutation,
  useBulkCreateMeasurementsMutation,
  useUpdateMeasurementMutation,
  useDeleteMeasurementMutation,
  useBulkUpdateCostCodesMutation,

  // Assemblies
  useGetAssembliesQuery,
  useLazyGetAssembliesQuery,
  useGetAssemblyQuery,
  useCreateAssemblyMutation,
  useUpdateAssemblyMutation,
  useDeleteAssemblyMutation,

  // Assembly Application
  useApplyAssemblyMutation,

  // Export
  useExportToEstimateMutation,
} = takeoffApi;
